import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery } from 'react-query'
import posthog from 'posthog-js'
import { CiSearch } from 'react-icons/ci'
import Header from 'components/headers/addLoan'
import { PulseCard, SectionError } from 'components/states'
import { getWithdrawalCartable } from 'services/deposits'
import { longJalali, toman } from 'utilities/helper/format'

/**
 * «درخواست‌های برداشت» — the manager's queue.
 *
 * A worklist, not a review screen: there is no accept and no reject, because
 * the money in question already belongs to the member. The only action is to
 * pay, and it hands off to the fund's existing «برداشت وجه» flow with the
 * amount and the request pre-filled, so every rial that leaves a fund still
 * goes through one code path.
 *
 * Rows are oldest-first. The person who has been waiting longest is the one
 * the fund is failing, and sorting by amount would quietly push small asks —
 * which are most of them — to the bottom forever.
 *
 * Each row states the member's capital AND what they owe the fund, with a
 * suggested figure of capital − debt. Suggested, not enforced: funds lend to
 * people in difficulty on purpose, and whether to net a member's debt off
 * their own capital is a judgement about that person, not a validation rule.
 */
export default function WithdrawalRequestsPage() {
    const params = useParams()
    const navigate = useNavigate()
    const [query, setQuery] = useState('')

    const moneypoolId = params?.id
    const { data, isLoading, isError, refetch } = useQuery(
        ['admin-withdrawal-requests', moneypoolId, query.trim()],
        getWithdrawalCartable,
        { enabled: Boolean(moneypoolId), staleTime: 30_000 }
    )

    const rows = data?.results ?? []

    // `withdrawal_requests_list_viewed` is fired by the two places that link
    // here, so it counts intent to open the queue and misses every manager who
    // arrived by URL, by back button, or by searching. This one counts the
    // screen actually rendering — including the empty queue, which is the
    // answer to «did anyone look and find nothing to pay?».
    useEffect(() => {
        if (!data) return
        posthog.capture('withdrawal_requests_list_rendered', {
            fund_id: moneypoolId,
            rows_count: rows.length,
            is_search: Boolean(query.trim()),
            has_debt_rows: rows.some((row) => row.debt > 0),
        })
        // Keyed on the fetched data, not on every re-render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data])

    return (
        <div className=" min-h-screen bg-canvas pb-10">
            <Header title="درخواست‌های برداشت" />

            <div className=" px-4 pt-4">
                <div className=" relative mb-3">
                    <CiSearch className=" absolute right-3 top-1/2 -translate-y-1/2 text-text-muted" />
                    <input
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        placeholder="جستجوی نام یا شماره"
                        className=" w-full rounded-xl border border-stroke bg-white py-2 pr-9 pl-3 text-xs outline-none focus:border-brand"
                    />
                </div>

                {isLoading && <PulseCard />}
                {isError && <SectionError onRetry={() => refetch()} />}

                {!isLoading && rows.length === 0 && (
                    <p className=" rounded-2xl border border-stroke bg-white p-6 text-center text-xs leading-6 text-text-secondary">
                        درخواست برداشت بازی وجود ندارد.
                    </p>
                )}

                <div className=" flex flex-col gap-3">
                    {rows.map((row) => (
                        <div
                            key={row.id}
                            className=" rounded-2xl border border-stroke bg-white p-4"
                        >
                            <div className=" flex items-start justify-between gap-2">
                                <div className=" min-w-0">
                                    <p className=" truncate text-sm font-bold text-heading">
                                        {row.member_name}
                                    </p>
                                    <p className=" mt-[2px] text-[11px] text-text-muted">
                                        ثبت‌شده در {longJalali(row.created)}
                                    </p>
                                </div>
                                <p className=" shrink-0 text-sm font-bold text-heading">
                                    {toman(row.amount)}
                                </p>
                            </div>

                            {row.paid_amount > 0 && (
                                <p className=" mt-2 text-[11px] text-success-on">
                                    {toman(row.paid_amount)} پرداخت شده ·
                                    باقیمانده {toman(row.remaining)}
                                </p>
                            )}

                            {row.user_description && (
                                <p className=" mt-2 rounded-lg bg-canvas px-3 py-2 text-[11px] leading-6 text-text-body">
                                    {row.user_description}
                                </p>
                            )}

                            <div className=" mt-3 flex items-center justify-between border-t border-divider pt-2 text-[11px] text-text-secondary">
                                <span>سرمایه: {toman(row.capital)}</span>
                                {row.debt > 0 && (
                                    <span className=" text-warning-on">
                                        بدهی: {toman(row.debt)}
                                    </span>
                                )}
                            </div>

                            {row.debt > 0 && (
                                <p className=" mt-2 rounded-lg bg-warning-soft px-3 py-2 text-[11px] leading-6 text-warning-on">
                                    این عضو {toman(row.debt)} بابت اقساط به
                                    صندوق بدهکار است. اگر بخواهید می‌توانید
                                    به‌جای کل مبلغ، {toman(row.suggested_max)}{' '}
                                    پرداخت کنید. تصمیم با شماست.
                                </p>
                            )}

                            {!row.partial_accepted && (
                                <p className=" mt-2 text-[11px] leading-6 text-text-muted">
                                    این عضو پرداخت بخشی از مبلغ را نپذیرفته
                                    است؛ باید همهٔ {toman(row.remaining)} را
                                    یکجا پرداخت کنید.
                                </p>
                            )}

                            <button
                                type="button"
                                onClick={() => {
                                    posthog.capture(
                                        'withdrawal_request_pay_started',
                                        {
                                            fund_id: moneypoolId,
                                            request_id: row.id,
                                        }
                                    )
                                    navigate(
                                        `/loans/withDraw/${moneypoolId}/${row.poolship_id}`,
                                        {
                                            state: {
                                                withdrawalRequest: row,
                                            },
                                        }
                                    )
                                }}
                                className=" mt-3 w-full rounded-full bg-green-1 py-2 text-sm font-bold text-white"
                            >
                                پرداخت
                            </button>
                        </div>
                    ))}
                </div>

                {rows.length > 0 && (
                    <p className=" mt-4 px-1 text-[11px] leading-6 text-text-muted">
                        این درخواست‌ها رد نمی‌شوند. اگر الآن پول کافی ندارید،
                        می‌توانید بعداً یا در چند نوبت پرداخت کنید؛ بستن درخواست
                        فقط با خود عضو است.
                    </p>
                )}
            </div>
        </div>
    )
}
